import React, { useState } from 'react';
import { Card, Badge, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faEye } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import { formatDate, truncateText, getResearchTypeLabel } from '../../../utils/helpers';
import { ACCESS_LEVELS } from '../../../utils/constants';
import { getDownloadUrl } from '../../../services/researchService';
import { useAuth } from '../../../hooks/useAuth';
import './ResearchCard.css';

const ResearchCard = ({ research }) => {
  const { isAuthenticated } = useAuth();
  const [downloading, setDownloading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const getAccessLevelBadgeVariant = (accessLevel) => {
    return accessLevel === ACCESS_LEVELS.PUBLIC ? 'success' : 'warning';
  };

  const getAccessLevelText = (accessLevel) => {
    return accessLevel === ACCESS_LEVELS.PUBLIC ? 'Public' : 'Restricted';
  };

  const handleDownload = async (e) => {
    e.preventDefault();
    setDownloading(true);

    try {
      const downloadUrl = await getDownloadUrl(research);

      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = research.file_name || 'research.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Failed to download file. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card className="research-card book-card h-100">
      {/* Book Cover/Spine - Always Visible */}
      <Card.Header className="book-header d-flex justify-content-between align-items-center">
        <Badge bg="info" className="type-badge">
          {getResearchTypeLabel(research.type)}
        </Badge>
        <Badge bg={getAccessLevelBadgeVariant(research.access_level)}>
          {getAccessLevelText(research.access_level)}
        </Badge>
      </Card.Header>

      {/* Closed Book Content - Always Visible */}
      <Card.Body className="book-closed-content">
        <Card.Title as="h5" className="research-title">
          <Link to={`/research/${research.id}`} className="research-link">
            {truncateText(research.title, 60)}
          </Link>
        </Card.Title>

        <Card.Subtitle className="mb-2 text-muted research-author">
          By {research.author}
        </Card.Subtitle>

        <div className="research-department">
          <small className="text-muted">
            {research.departments?.name}
          </small>
        </div>
      </Card.Body>

      {/* Open Book Content - Revealed on Hover */}
      <div className="book-open-content">
        <Card.Text className="research-abstract">
          <span
            onClick={() => setIsExpanded(!isExpanded)}
            style={{ cursor: 'pointer' }}
            title="Click to expand/collapse"
          >
            {isExpanded ? research.abstract : truncateText(research.abstract, 100)}
            {research.abstract.length > 100 && (
              <span className="text-primary ms-1" style={{ fontSize: '0.9em' }}>
                {isExpanded ? '(Show less)' : '... (Read more)'}
              </span>
            )}
          </span>
        </Card.Text>

        <div className="research-meta">
          <small className="text-muted">
            {(() => {
              const keywords = research.keywords || '';
              const streamMatch = keywords.match(/stream:([^,]+)/);
              if (streamMatch) {
                return <span>{streamMatch[1].trim()}</span>;
              }
              return null;
            })()}
            {(() => {
              const keywords = research.keywords || '';
              const levelMatch = keywords.match(/level:([^,]+)/);
              if (levelMatch) {
                const levelValue = levelMatch[1].trim();
                const formattedLevel = levelValue.charAt(0).toUpperCase() + levelValue.slice(1);
                return <span> • {formattedLevel}</span>;
              }
              return null;
            })()}
            {research.year && <span> • {research.year}</span>}
          </small>
        </div>

        <Card.Footer className="book-footer">
          <small className="text-muted">
            {formatDate(research.created_at)}
          </small>

          <div className="research-actions">
            <Button
              as={Link}
              to={`/research/${research.id}`}
              variant="outline-primary"
              size="sm"
              disabled={downloading}
            >
              <FontAwesomeIcon icon={faEye} className="me-1" />
              View
            </Button>

            {research.file_url && (
              (research.access_level === 'public' || (research.access_level === 'restricted' && isAuthenticated)) && (
                <Button
                  variant="outline-success"
                  size="sm"
                  onClick={handleDownload}
                  disabled={downloading}
                >
                  <FontAwesomeIcon icon={faDownload} className="me-1" />
                  {downloading ? 'Downloading...' : 'Download'}
                </Button>
              )
            )}
          </div>
        </Card.Footer>
      </div>
    </Card>
  );
};

export default ResearchCard;